import { useMfa } from '@iheartjane/menu';
import { Box, Flex, Link, Loading, Typography } from '@iheartjane/reefer';
import { Form, FormFieldWrapper } from '@iheartjane/reefer-hook-form';
import { useEffect } from 'react';

import { formatPhone } from '@jane/shared/util';

import { MultifactorVerificationInput } from './MultifactorVerificationInput';

interface MultifactorAuthenticationProps {
  onSuccess?: () => void;
  phoneNumber: string;
}

export const MultifactorAuthentication = ({
  onSuccess,
  phoneNumber,
}: MultifactorAuthenticationProps) => {
  const {
    canResendCode,
    canSendCode,
    codeResent,
    errorSendingCode,
    errorVerifyingCode,
    resendCode,
    sendCode,
    sendingCode,
    verifyCode,
  } = useMfa();

  useEffect(() => {
    if (canSendCode()) sendCode();
  }, [canSendCode, sendCode]);

  const showResendButton = canResendCode();

  const onSubmit = async (values: { code: string }) => {
    const success = await verifyCode(values.code);
    if (success) onSuccess?.();
  };

  return (
    <Form onSubmit={onSubmit} name="multifactor-authentication">
      <Flex p={24} flexDirection="column">
        <Typography branded mb={16} variant="h2-semibold">
          Verify your identity
        </Typography>
        {sendingCode && (
          <Box mt={24} py={48} position="relative">
            <Loading color="black" size="lg" />
          </Box>
        )}
        <Typography mb={16}>
          Enter the code sent to your phone number {formatPhone(phoneNumber)}
        </Typography>
        <Flex mb={24} width="100%" justifyContent="center">
          <FormFieldWrapper
            defaultValue=""
            name="code"
            required
            minLength={6}
            render={({ field }) => (
              <MultifactorVerificationInput
                error={errorVerifyingCode}
                field={field}
              />
            )}
          />
        </Flex>
        {errorSendingCode &&
          (errorSendingCode.includes('wrong scope') ? (
            <Typography color="error" textAlign="center" as="span" mb={16}>
              Something went wrong. Please{' '}
              {!codeResent ? 'resend the code and' : ''} try again or{' '}
              <Link
                color="error"
                href="https://customer-support.iheartjane.com/s/contactsupport"
              >
                contact support.
              </Link>
            </Typography>
          ) : (
            <Typography color="error" textAlign="center" mb={16}>
              {errorSendingCode}
            </Typography>
          ))}

        {errorVerifyingCode && (
          <Typography color="error" textAlign="center" mb={16}>
            {errorVerifyingCode}
          </Typography>
        )}
        <Form.SubmitButton full label="Verify" mb={16} size="small" />

        {showResendButton && (
          <Flex alignSelf="center">
            <Link onClick={resendCode} variant="underline">
              Send code again
            </Link>
          </Flex>
        )}
      </Flex>
    </Form>
  );
};
